/* eslint-disable react/require-default-props */
import React, { useMemo, useState } from 'react';
import { Calendar, Formats, SlotInfo, View } from 'react-big-calendar';
import { Box, Skeleton, Typography, useMediaQuery } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import {
  format,
  startOfWeek,
  getDay,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  startOfDay,
} from 'date-fns';
import { Trips } from '@prisma/client';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Appointment, LoadedUser, UnavailableTimes } from '../../../interfaces';
import useGetBatchTimeBlocks from '../../../hooks/employees/useGetBatchTimeBlocks';
import useGetBatchTrips from '../../../hooks/employees/useGetBatchTrips';
import useGetBusinessFlashDays from '../../../hooks/flashbooks/useGetBusinessFlashDays';
import useUser from '../../../hooks/global/useUser';
import {
  CalendarEvent,
  CalendarEventType,
  CalendarSlotSelection,
  Resource,
} from '../../../types/calendar';
import useSizeOptions from '../../../hooks/tattoos/useSizeOptions';
import {
  filterAppointmentsToRange,
  getCalendarEvents,
  getDateFnsLocalizer,
  getEarliestAvailabilities,
  getVisibleRange,
  slotPropGetter,
} from '../../../helpers/calendar';
import { getEventStyle } from '../../../helpers/calendar/eventStyle';
import useOnschedResourceBatch from '../../../hooks/employees/useOnschedResourceBatch';
import './styles.css';
import { ResourceBlock } from '../../../types/employees';
import TripSideBarModal from '../../trips/TripSideBarModal';
import TimeBlockSideBarModal from '../../timeBlocks/TimeBlockSideBarModal';
import { cx, openStorageUrl } from '../../../helpers/global';
import { getEmployeesResourcesList } from '../../../services/business';
import { convertIndexToShortDay } from '../../../utils/global/helper';
import { withSelectedArtists } from './ResourceTitle';
import useGetUnavailabilityBatch from '../../../hooks/availability/useGetUnavailabilityBatch';
import ExternalEventSideBarModal from '../ExternalEventSideBarModal';
import { stripUnassignedId } from '../../../constants/calendar';
import useCashDiscount from '../../../hooks/payments/useCashDiscount';
import useBusinessWebRedesign from '../../../hooks/businesses/useBusinessWebRedesign';
import useGetWaivers from '../../../hooks/waivers/useGetWaivers';
import WaiverIcon from '../../icons/WaiverIcon';
import useCalendarSelectedAppointment from '../../../hooks/appointments/useCalendarSelectedAppointment';
import { getWaiverRequestStatus } from '../../../helpers/waivers';
import useGetUnreadRequests from '../../../hooks/requests/useGetUnreadRequests';

const localizer = getDateFnsLocalizer();

interface CalendarProps {
  artistOptions: string[];
  appointments?: Appointment[];
  selectedArtists: string[];
  setSelectedArtists: React.Dispatch<React.SetStateAction<string[]>>;
  selectedEvents: CalendarEventType[];
  view: View;
  setView: React.Dispatch<React.SetStateAction<View>>;
  date: Date;
  setDate: (newDate: Date) => void;
  openAppointment: (appointment: Appointment) => void;
  refreshModal: (appointmentId: string) => void;
  includeInactiveEmployees?: boolean;
  onSelectSlot?: (slot: CalendarSlotSelection) => void;
  openedAppointmentId?: string;
}

interface SelectedTimeBlock extends ResourceBlock {
  employeeId: string;
}

interface EventComponentProps {
  event: CalendarEvent;
}

function ExternalEventLabel() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 15,
        padding: '0 6px',
        borderRadius: 50,
        background: '#fff',
        border: '1px solid var(--nonstatus-external-border)',
        fontSize: 9,
        fontWeight: 700,
        lineHeight: 'normal',
        color: 'rgb(102,102,102)',
        marginBottom: 2,
      }}>
      Google
    </span>
  );
}

// Both icon and dot render always; styles.css toggles which is visible.
function AppointmentWaiverIcon({
  color,
  onClick,
}: {
  color: string;
  onClick?: () => void;
}) {
  const Wrapper = onClick ? 'button' : 'span';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      aria-label={onClick ? 'Waiver' : undefined}
      onClick={
        onClick
          ? event => {
              event.stopPropagation();
              onClick();
            }
          : undefined
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        padding: 0,
        border: 0,
        background: 'none',
        color: 'inherit',
        cursor: onClick ? 'pointer' : undefined,
      }}>
      <WaiverIcon
        className="appt-waiver-icon"
        width={14}
        height={14}
        fill={color}
        style={{ flexShrink: 0 }}
      />
      <span
        className="appt-waiver-dot"
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
          marginTop: 4,
        }}
      />
    </Wrapper>
  );
}

function getAppointmentClasses(event: CalendarEvent) {
  if (event.type !== 'appointment') return [];
  return [
    event.serviceCategory &&
      `service-${event.serviceCategory.toLowerCase().replace('_', '-')}`,
    event.appointmentType &&
      `type-${event.appointmentType.toLowerCase().replace(/_/g, '-')}`,
    event.isVirtual && 'virtual',
    event.checkedOut && !event.canceled && 'checked-out',
    // ENG-2793: additive — only set when the redesign flag is active.
    event.statusKey && `status-${event.statusKey.toLowerCase()}`,
  ];
}

function CheckedOutBadge() {
  return (
    <span
      aria-label="Checked out"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        borderRadius: '50%',
        backgroundColor: '#ffffff',
        border: '1.5px solid #2e7d32',
        boxSizing: 'border-box',
        flexShrink: 0,
        marginRight: 4,
      }}>
      <CheckIcon
        sx={{
          fontSize: 9,
          color: '#2e7d32',
          strokeWidth: 1,
          stroke: '#2e7d32',
        }}
      />
    </span>
  );
}

function isCheckedOut(event: CalendarEvent) {
  return event.type === 'appointment' && event.checkedOut && !event.canceled;
}

function NewBadge() {
  return (
    <span
      aria-label="New"
      style={{
        position: 'absolute',
        right: 7,
        bottom: 6,
        display: 'inline-flex',
        alignItems: 'center',
        height: 16,
        padding: '0 8px',
        borderRadius: 50,
        background: 'rgb(255,48,8)',
        fontSize: 9,
        fontWeight: 700,
        lineHeight: 'normal',
        color: '#fff',
      }}>
      New
    </span>
  );
}

function MonthEvent({ event }: EventComponentProps) {
  if (!event.start) return null;
  return (
    <div
      className={cx(
        `${event.type}-event`,
        event.type === 'appointment' && event.canceled && 'canceled',
        ...getAppointmentClasses(event),
      )}
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: `${
          event.type === 'appointment' && !event.canceled ? '3px' : '2px'
        } 2px`,
        overflow: 'hidden',
      }}>
      {isCheckedOut(event) && <CheckedOutBadge />}
      <Typography
        variant="caption"
        style={{ marginRight: 4 }}
        color={event.textColor || 'white'}>
        {format(event.start, 'h:mma')} •
      </Typography>
      <Typography
        variant="h5"
        color={event.textColor || 'white'}
        style={{ fontSize: 12 }}>
        {event.title}
      </Typography>
    </div>
  );
}

function ArtistChip({
  artist,
  textColor,
}: {
  artist: Resource;
  textColor?: string;
}) {
  return (
    <div
      className="appt-artist-chip"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        marginTop: 'auto',
        minWidth: 0,
      }}>
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: artist.color || '#333',
          color: '#fff',
          fontSize: 8,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
        {artist.initials}
      </span>
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: textColor,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
        {artist.resourceTitle}
      </span>
    </div>
  );
}

function WeekEvent({
  event,
  isNew,
  artist,
  onWaiverClick,
}: EventComponentProps & {
  isNew?: boolean;
  artist?: Resource;
  onWaiverClick?: (event: CalendarEvent) => void;
}) {
  return (
    <div
      className={cx(
        `${event.type}-event`,
        event.type === 'appointment' && event.canceled && 'canceled',
        ...getAppointmentClasses(event),
      )}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        overflow: 'hidden',
        // Classic keeps its fixed offset; redesign gets its rhythm from
        // --appt-gap/--appt-margin-y (styles.css), which also centers this
        // div at the 15-min tier via margin: auto.
        marginTop:
          event.type === 'appointment' && event.statusKey
            ? 'var(--appt-margin-y, 0px)'
            : event.allDay
            ? undefined
            : 5,
        marginBottom:
          event.type === 'appointment' && event.statusKey
            ? 'var(--appt-margin-y, 0px)'
            : undefined,
        gap: 'var(--appt-gap, 6px)',
      }}>
      {isNew && <NewBadge />}
      {event.type === 'unavailable-time' && event.showSourceLabel && (
        <ExternalEventLabel />
      )}
      <div
        style={{
          display: 'flex',
          // Centers at the 15-min tier, where this is the only row left
          // (--appt-title-row-align override at max-height:32px).
          alignItems: 'var(--appt-title-row-align, flex-start)',
          gap: 'var(--appt-gap, 6px)',
          width: '100%',
          minWidth: 0,
          flexShrink: 0,
        }}>
        {isCheckedOut(event) && <CheckedOutBadge />}
        <Typography
          variant="h5"
          color={event.textColor || 'white'}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            minWidth: 0,
            ...(event.type === 'appointment' &&
              event.statusKey && {
                fontSize: 'var(--appt-title-size, 14px)',
                fontWeight: 700,
                lineHeight: 1.2,
              }),
          }}>
          {event.title}
        </Typography>
        {event.type === 'appointment' && event.statusKey && event.start && (
          <Typography
            className="appt-start-title"
            variant="body2"
            color={event.textColor}
            style={{
              fontSize: 'var(--appt-time-size, 11px)',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
            {format(event.start, 'h:mm a')}
          </Typography>
        )}
        {event.type === 'appointment' && event.waiverIconColor && (
          <AppointmentWaiverIcon
            color={event.waiverIconColor}
            onClick={onWaiverClick ? () => onWaiverClick(event) : undefined}
          />
        )}
      </div>
      {event.type === 'appointment' &&
        event.statusKey &&
        event.start &&
        event.end && (
          <div className="appt-time-row">
            <Typography
              className="appt-time-range"
              variant="body2"
              color={event.textColor}
              style={{
                fontSize: 'var(--appt-time-size, 11px)',
                fontWeight: 600,
                lineHeight: 'normal',
                flexShrink: 0,
              }}>
              {format(event.start, 'h:mm a')} – {format(event.end, 'h:mm a')}
            </Typography>
            {event.priceText && (
              <Typography
                className="appt-price-time"
                variant="body2"
                color={event.textColor}
                style={{
                  fontSize: 'var(--appt-price-size, 12px)',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}>
                {event.priceText}
              </Typography>
            )}
            {event.isMinor && (
              <span
                className="appt-minor"
                style={{
                  borderColor: event.textColor,
                  color: event.textColor,
                }}>
                Minor
              </span>
            )}
          </div>
        )}
      {event.detailsComponent}
      {artist && <ArtistChip artist={artist} textColor={event.textColor} />}
    </div>
  );
}

function withEventExtras({
  unreadAppointmentIds,
  resourceMap,
  showArtistChip,
  onWaiverClick,
}: {
  unreadAppointmentIds: Set<string>;
  resourceMap: Resource[];
  showArtistChip: boolean;
  onWaiverClick: (event: CalendarEvent) => void;
}) {
  // eslint-disable-next-line react/function-component-definition
  return function WeekEventWithExtras({ event }: EventComponentProps) {
    const isAppointment = event.type === 'appointment';
    const artist =
      showArtistChip && isAppointment
        ? resourceMap.find(resource => resource.resourceId === event.resourceId)
        : undefined;
    return (
      <WeekEvent
        event={event}
        isNew={isAppointment && unreadAppointmentIds.has(event.appointmentId)}
        artist={artist}
        onWaiverClick={onWaiverClick}
      />
    );
  };
}

export default function WeekView({
  artistOptions,
  appointments,
  selectedArtists,
  setSelectedArtists,
  selectedEvents,
  view,
  date,
  setDate,
  setView,
  openAppointment,
  refreshModal,
  includeInactiveEmployees = false,
  onSelectSlot,
  openedAppointmentId,
}: CalendarProps) {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const { active: redesignActive } = useBusinessWebRedesign();
  const { data: waivers } = useGetWaivers({ enabled: redesignActive });
  const { data: unreadRequests } = useGetUnreadRequests({
    enabled: redesignActive,
  });
  const unreadAppointmentIds = useMemo(
    () => new Set(unreadRequests?.scheduledAppointments ?? []),
    [unreadRequests],
  );

  const dataStartDate = React.useMemo(
    () => startOfWeek(startOfMonth(date)),
    [date],
  );
  const dataEndDate = React.useMemo(() => endOfWeek(endOfMonth(date)), [date]);

  const { user: loadedUser } = useUser<LoadedUser>();
  const { getAllInPrice } = useCashDiscount();

  const realSelectedArtists = useMemo(
    () => stripUnassignedId(selectedArtists) ?? [],
    [selectedArtists],
  );
  const realArtistOptions = useMemo(
    () => stripUnassignedId(artistOptions) ?? [],
    [artistOptions],
  );

  const { data: timeBlocks } = useGetBatchTimeBlocks({
    employeeIds: realSelectedArtists,
    dataStartDate,
    dataEndDate,
  });
  const { data: trips } = useGetBatchTrips({
    employeeIds: realSelectedArtists,
  });
  const { data: flashDays } = useGetBusinessFlashDays();
  const {
    data: employeesOnschedResources,
    isLoading: isLoadingResources,
    isError: isResourcesError,
  } = useOnschedResourceBatch({
    employeeIds: realArtistOptions,
  });
  const { data: unavailableTimes } = useGetUnavailabilityBatch({
    startDate: dataStartDate.toISOString(),
    endDate: dataEndDate.toISOString(),
    employeeIds: realSelectedArtists,
  });

  const { sizeOptions } = useSizeOptions();

  const [selectedTrip, setSelectedTrip] = useState<Trips | null>(null);
  const [selectedTimeBlock, setSelectedTimeBlock] =
    useState<SelectedTimeBlock | null>(null);
  const [selectedUnavailableTime, setSelectedUnavailableTime] = useState<{
    unavailableTime: UnavailableTimes;
    employeeId: string;
  } | null>(null);

  const visibleRange = useMemo(() => getVisibleRange(view, date), [view, date]);

  const visibleAppointments = useMemo(
    () => filterAppointmentsToRange(appointments, visibleRange),
    [appointments, visibleRange],
  );

  const hasUnassignedAppointment = useMemo(
    () => visibleAppointments.some(a => !a.employeeId),
    [visibleAppointments],
  );

  const resourceMap = React.useMemo(() => {
    const list = getEmployeesResourcesList(
      loadedUser,
      hasUnassignedAppointment,
      includeInactiveEmployees ? visibleAppointments : [],
    );
    if (selectedArtists.length === 1 && selectedArtists[0] === 'Initialize') {
      return list;
    }
    // Inactive lanes aren't in the artist filter, so let them through.
    return list.filter(
      r => r.isInactive || selectedArtists.includes(r.resourceId),
    );
  }, [
    loadedUser,
    selectedArtists,
    hasUnassignedAppointment,
    visibleAppointments,
    includeInactiveEmployees,
  ]);

  const scrollToTime = React.useMemo(() => {
    if (!employeesOnschedResources || view === 'month') return undefined;

    const earliestAvailabilities = getEarliestAvailabilities(
      employeesOnschedResources,
      selectedArtists,
    );

    if (view === 'day') {
      const dayIndex = getDay(date);
      const day = convertIndexToShortDay(dayIndex);
      const earliestDate = earliestAvailabilities[day];
      if (!earliestDate) return startOfDay(date);
      return earliestDate;
    }

    if (view === 'week') {
      const minimum = Object.values(earliestAvailabilities).reduce<Date | null>(
        (min, currentDate) =>
          !min || currentDate.getTime() < min.getTime() ? currentDate : min,
        null,
      );
      return minimum ?? startOfDay(date);
    }

    return undefined;
  }, [date, employeesOnschedResources, selectedArtists, view]);

  const events = getCalendarEvents({
    appointments: appointments ?? [],
    trips,
    flashDays,
    timeBlocks,
    unavailableTimes,
    resourceMap,
    sizeOptions,
    employeesToBusiness: loadedUser.business.employees,
    selectedEvents,
    getAllInPrice,
    redesignActive,
    waivers,
  });

  const { setOpenedAppointmentIntent } = useCalendarSelectedAppointment();

  const handleClick = (event: CalendarEvent) => {
    if (event.type === 'appointment') {
      const appointment = appointments?.find(
        apt => apt.appointmentId === event.appointmentId,
      );
      if (!appointment) return;
      openAppointment(appointment);
      refreshModal(appointment.appointmentId); // always get fresh data
    } else if (event.type === 'trip') {
      const trip = trips?.[event.resourceId]?.find(
        t => t.tripId === event.tripId,
      );
      if (!trip) return;
      setSelectedTrip(trip);
    } else if (event.type === 'time-block') {
      const timeBlock = timeBlocks?.[event.resourceId]?.find(
        tb => tb.id === event.timeBlockId,
      );
      if (!timeBlock) return;
      setSelectedTimeBlock({ ...timeBlock, employeeId: event.resourceId });
    } else if (event.type === 'unavailable-time') {
      const unavailableTime = unavailableTimes?.[event.resourceId]?.find(
        ut => ut.id === event.unavailableTimeId,
      );
      if (!unavailableTime) return;
      setSelectedUnavailableTime({
        unavailableTime,
        employeeId: event.resourceId,
      });
    }
  };

  const handleWaiverIconClick = (event: CalendarEvent) => {
    if (event.type !== 'appointment') return;
    const appointment = appointments?.find(
      apt => apt.appointmentId === event.appointmentId,
    );
    const waiverRequest = appointment?.waiverRequest;
    // Opening the file here keeps it inside the click gesture, so the browser
    // allows the new tab instead of falling back to a same-tab redirect.
    if (
      appointment &&
      waiverRequest?.signedFileLink &&
      getWaiverRequestStatus({ appointment, waiverRequest, waivers }) ===
        'complete'
    ) {
      openStorageUrl(waiverRequest.signedFileLink);
      return;
    }
    setOpenedAppointmentIntent('waiver');
    handleClick(event);
  };

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    if (!onSelectSlot) return;
    const { resourceId, start, end, action } = slotInfo;
    // Unassigned/inactive lanes still open the form, just without an artist.
    const isActiveEmployee =
      !!resourceId &&
      loadedUser.business.employees?.some(e => e.employeeId === resourceId);
    onSelectSlot({
      start,
      end: action === 'select' ? end : undefined,
      employeeId: isActiveEmployee ? (resourceId as string) : undefined,
    });
  };

  const eventStyleGetter = (event: CalendarEvent) =>
    getEventStyle(event, redesignActive, openedAppointmentId);

  const formats: Formats = {
    weekdayFormat: (d, culture, l) => l?.format(d, 'eee', culture) || '',

    dateFormat: (d, culture, l) => l?.format(d, 'd', culture) || '',

    dayFormat: (d, culture, l) => l?.format(d, 'eee M/d', culture) || '',

    timeGutterFormat: (d, culture, l) => l?.format(d, 'h aaa', culture) || '',
  };

  const updatedSelectedTimeBlock = useMemo(() => {
    if (!selectedTimeBlock) return null;
    return Object.entries(timeBlocks).reduce<
      (ResourceBlock & { employeeId: string }) | null
    >((acc, [employeeId, blocks]) => {
      const block = blocks.find(tb => tb.id === selectedTimeBlock.id);
      if (block) {
        return { ...block, employeeId };
      }
      return acc;
    }, null);
  }, [selectedTimeBlock, timeBlocks]);

  // Fallback UI for when appointments is undefined
  if (!appointments || (isLoadingResources && !isResourcesError)) {
    return (
      <div style={{ height: '700px', margin: '40px 0' }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={80}
          style={{ marginBottom: 10 }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={80}
          style={{ marginBottom: 10 }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={80}
          style={{ marginBottom: 10 }}
        />
      </div>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: {
            xs: 'calc(100svh - 355px)',
            md: 620,
          },
        }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{
            height: isDesktop ? 620 : 'calc(100svh - 355px)',
          }}
          className={cx(view, redesignActive && 'calendar-redesign')}
          step={view === 'day' ? 15 : 30}
          timeslots={view === 'day' ? 4 : 2}
          date={date}
          view={view}
          onNavigate={newDate => setDate(newDate)}
          onView={newView => setView(newView)}
          eventPropGetter={eventStyleGetter}
          resourceIdAccessor="resourceId"
          resourceTitleAccessor="resourceTitle"
          resources={
            view === 'day' || (view === 'week' && resourceMap?.length === 1)
              ? resourceMap
              : undefined
          }
          toolbar={false}
          formats={formats}
          onSelectEvent={event => handleClick(event)}
          selectable={view === 'day' && !!onSelectSlot}
          onSelectSlot={handleSelectSlot}
          components={{
            month: { event: MonthEvent },
            week: {
              event: withEventExtras({
                unreadAppointmentIds,
                resourceMap,
                showArtistChip: redesignActive,
                onWaiverClick: handleWaiverIconClick,
              }),
            },
            day: {
              event: withEventExtras({
                unreadAppointmentIds,
                resourceMap,
                showArtistChip: false,
                onWaiverClick: handleWaiverIconClick,
              }),
            },
            resourceHeader: withSelectedArtists(
              setSelectedArtists,
              redesignActive,
            ),
          }}
          allDayMaxRows={1}
          showMultiDayTimes
          slotPropGetter={(d, resourceId) =>
            slotPropGetter(
              d,
              resourceId,
              employeesOnschedResources,
              timeBlocks,
              redesignActive,
            )
          }
          dayPropGetter={() => ({
            style: {
              backgroundColor: 'white',
            },
          })}
          scrollToTime={scrollToTime}
        />
      </Box>
      <TripSideBarModal
        trip={selectedTrip}
        onClose={() => setSelectedTrip(null)}
      />
      <TimeBlockSideBarModal
        selectedArtists={selectedArtists}
        timeBlock={updatedSelectedTimeBlock}
        onClose={() => setSelectedTimeBlock(null)}
      />
      <ExternalEventSideBarModal
        unavailableTimeData={selectedUnavailableTime}
        onClose={() => setSelectedUnavailableTime(null)}
      />
    </>
  );
}
